'use client';

import { FC, useCallback, useEffect } from 'react';
import { logger } from '@tuic/logger';

/**
 * A component which scrolls elements into view.
 * Add `data-scroll-id` to any element & add that scroll id to your URL's hash to auto scroll to that element on page load.
 *
 * ```tsx
 * // example.com/index#my-scroll-anchor
 * <div data-scroll-id="my-scroll-anchor"></div>
 * ```
 */
export const IntoViewScroller: FC = () => {
  let hash = typeof window !== 'undefined' ? window.location.hash : undefined;
  let handleClick = useCallback((ev: MouseEvent) => {
    let anchor = ev.target as HTMLAnchorElement | null;
    if (anchor?.nodeName?.toLowerCase() !== 'a') {
      anchor = anchor?.parentElement as HTMLAnchorElement | null;
    }
    if (anchor?.nodeName?.toLowerCase() !== 'a') {
      return;
    }
    let hash = anchor.getAttribute('href')?.split('#')?.[1];
    if (typeof hash === 'undefined') {
      return;
    }
    attemptToScroll(hash, ev);
  }, []);
  useEffect(() => {
    // Scroll to the element on page load (start)
    attemptToScroll(hash, undefined, 'start');
  }, [hash]);
  useEffect(() => {
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [handleClick]);
  return null;
};
const attemptToScroll = (hash: string | undefined, clickEvent?: MouseEvent, pos?: ScrollLogicalPosition) => {
  if (typeof hash === 'undefined' || hash === '' || hash === '#') {
    return;
  }
  const query = hash.substring(0, 1) === '#' ? hash.substring(1) : hash;
  if (query.length < 1) {
    return;
  }
  try {
    const element = findVisibleElement(`[data-scroll-id="${query}"]`) ?? findVisibleElement(`#${query}`) ?? findVisibleElement(`.${query}`) ?? findVisibleElement(query);
    if (!element) {
      return;
    }
    if (clickEvent) {
      clickEvent.preventDefault();
    }
    checkContentStability(() => element.scrollIntoView({
      behavior: 'smooth',
      block: pos ?? 'start'
    }));
  } catch (e) {
    logger.error(e);
  }
};

// execute the callback if the layout shift due to loading content is finished
function checkContentStability(callback: () => void, checkInterval: number = 100, stabilityThreshold: number = 1000): () => void {
  let lastHeight = document.body.offsetHeight;
  let lastWidth = document.body.offsetWidth;
  let stableCount = 0;
  let intervalId: number;
  function checkStability() {
    const currentHeight = document.body.offsetHeight;
    const currentWidth = document.body.offsetWidth;
    if (currentHeight === lastHeight && currentWidth === lastWidth) {
      stableCount += checkInterval;
      if (stableCount >= stabilityThreshold) {
        clearInterval(intervalId);
        callback();
      }
    } else {
      stableCount = 0;
      lastHeight = currentHeight;
      lastWidth = currentWidth;
    }
  }
  intervalId = window.setInterval(checkStability, checkInterval);
  return () => {
    clearInterval(intervalId);
  };
}

// Leftover approach that might be usefull at some point
// function observeElementVisibility(
//   element: Element,
//   callback: () => void,
//   threshold: number = 0.1,
// ): () => void {
//   // check if the element is visible in the viewport
//   const observer = new IntersectionObserver(
//     entries => {
//       entries.forEach(entry => {
//         if (!entry.isIntersecting) {
//           callback();
//         }
//       });
//     },
//     { threshold },
//   );

//   observer.observe(element);

//   function handleScroll() {
//     observer.disconnect();
//     window.removeEventListener('scroll', handleScroll);
//     logger.debug('User scrolled, observer disconnected');
//   }

//   // Issue: The scroll event listener will also fire when scrollIntoView is called
//   window.addEventListener('scroll', () => {
//       handleScroll();
//   });

//   return () => {
//     observer.disconnect();
//     window.removeEventListener('scroll', handleScroll);
//     logger.debug('Observer manually disconnected');
//   };
// }

const isElementNotHidden = (el: Element): boolean => {
  let currentEl: Element | null = el;
  while (currentEl) {
    if (window.getComputedStyle(currentEl).display === 'none') {
      return false;
    }
    currentEl = currentEl.parentElement;
  }
  return true;
};
const findVisibleElement = (selector: string): Element | undefined => {
  const elements = window.document.querySelectorAll(selector);
  return Array.from(elements).find(isElementNotHidden);
};